import { useEffect, useState } from "react";

import { api, clinicHeader, readError } from "../api/client";
import { ErrorNote } from "./ErrorNote";

export type Slot = {
  id: string;
  staff_id: string;
  staff_name: string;
  start_datetime: string;
  end_datetime: string;
  status: string;
};

type Props = {
  /** Show one doctor's slots first. The list still offers the others. */
  preferStaffId?: string;
  onPick: (slot: Slot) => void;
  busy?: boolean;
};

function dayLabel(iso: string) {
  return new Date(iso).toLocaleDateString([], {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

function clock(iso: string) {
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/**
 * What is open, grouped by day.
 *
 * The clinic comes from the session and is never a parameter, so there is
 * nothing here to point at another clinic's calendar.
 *
 * A slot shown here can be gone by the time somebody clicks it. That is the
 * ordinary case rather than an edge one, and the answer is not to refresh
 * faster: the claim is two-phase on the server, and a lost race comes back with
 * other times (SCH-F-021). This list only has to be nearly right.
 */
export function SlotPicker({ preferStaffId, onPick, busy }: Props) {
  const [slots, setSlots] = useState<Slot[]>([]);
  const [onlyThisDoctor, setOnlyThisDoctor] = useState(Boolean(preferStaffId));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      const { data, error: failed } = await api.GET("/portal/slots", {
        params: {
          header: clinicHeader(),
          query: onlyThisDoctor && preferStaffId ? { staff_id: preferStaffId } : {},
        },
      });
      if (cancelled) return;
      setLoading(false);
      if (failed) {
        setError(readError(failed).message);
        return;
      }
      setError(null);
      setSlots((data?.items ?? []) as Slot[]);
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [onlyThisDoctor, preferStaffId]);

  const byDay = new Map<string, Slot[]>();
  for (const slot of slots) {
    const key = slot.start_datetime.slice(0, 10);
    byDay.set(key, [...(byDay.get(key) ?? []), slot]);
  }

  return (
    <div className="slot-picker">
      <ErrorNote message={error} />

      {preferStaffId && (
        <label className="checkbox">
          <input
            type="checkbox"
            checked={onlyThisDoctor}
            onChange={(event) => setOnlyThisDoctor(event.target.checked)}
          />
          Only this doctor
        </label>
      )}

      {loading && <p className="empty">Reading the calendar…</p>}

      {!loading && slots.length === 0 && (
        <p className="empty">
          Nothing open. Somebody has to add a working window before this clinic
          takes bookings.
        </p>
      )}

      {[...byDay.entries()].map(([day, rows]) => (
        <div key={day} className="slot-day">
          {/* The key is the date part of the first slot, so it labels the day
              without reaching back into the array. */}
          <h3>{dayLabel(`${day}T12:00:00`)}</h3>
          <div className="slot-grid">
            {rows.map((slot) => (
              <button
                key={slot.id}
                type="button"
                className="slot"
                disabled={busy}
                onClick={() => onPick(slot)}
                // The label carries the time first. Without it a screen reader
                // announces the doctor's name, because that is the only text
                // when the list is filtered to one doctor.
                aria-label={`${clock(slot.start_datetime)} with ${slot.staff_name}`}
                title={slot.staff_name}
              >
                <span className="slot-time">{clock(slot.start_datetime)}</span>
                {!onlyThisDoctor && <span className="slot-who">{slot.staff_name}</span>}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
